import ApiErrorBanner from '@/components/ApiErrorBanner';
import { ApiErrorType } from '@/components/ApiErrorBanner/ApiErrorBanner';
import StudyUsers from '@/components/Studies/Show/Users';
import { DateTimeRenderer } from '@/components/UI/DateRenderers/DateRenderers';
import Link from '@/components/UI/Link';
import APITable from '@/components/UI/Table/Reusable/APITable';
import type { TanStackColumnDef, FilterOption } from '@/components/UI/Table/TableComponent.model';
import { _isNotEmpty, _notNil } from '@/littledash';
import type { Study } from '@/model/Study.model';
import { statusToTextMap } from '@/referenceData/study/status';
import { notAborted } from '@/support/Hooks/fetch/useAbortController';
import { api as apiRoutes, web as webRoutes } from '@/support/route';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { StudyTableProps } from './Dashboard.model';
import {
  generateColumns,
  generateStudiesMetadataColumns,
  studiesfilterOptions,
  updateColumns,
} from './Dashboard.utils';
import { createMultiSelectDoubleEncodingProcessor } from '@/utils/TableFilter.utils';
import { useFetchCollection } from '@/support/Hooks/fetch';
import type { StudyForm } from 'model/StudyForm.model';
import Truncate from '@/components/UI/Truncate/Truncate';

const initColumns: Array<TanStackColumnDef<Study>> = [
  {
    id: 'name',
    header: 'Name',
    accessorKey: 'name',
    enableSorting: true,
    isVisible: true,
    cell: (props) => {
      const original = props.row.original;
      return (
        <Link
          className="link w-100"
          to={webRoutes('studies.show', {
            id: original.id,
          })}
        >
          <Truncate tooltip={original.name}>{original.name}</Truncate>
        </Link>
      );
    },
  },
  {
    id: 'code',
    header: 'Study Code',
    accessorKey: 'code',
    enableSorting: true,
    isVisible: false,
  },
  {
    id: 'type',
    header: 'Type',
    isVisible: true,
    enableSorting: true,
    accessorKey: 'settings.title' as keyof Study,
  },
  {
    id: 'team',
    header: 'Team',
    isVisible: true,
    enableSorting: false,
    accessorKey: 'users',
    cell: (props) => <StudyUsers study={props.row.original} />,
  },
  {
    id: 'status',
    header: 'Status',
    accessorKey: 'status',
    isVisible: true,
    enableSorting: true,
    // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
    cell: (props) => statusToTextMap[props.row.original.status] || '',
  },
  {
    id: 'created_at',
    header: 'Created',
    accessorKey: 'created_at',
    enableSorting: true,
    isVisible: true,
    cell: (props) => <DateTimeRenderer value={props.row.original.created_at} />,
  },
];

const ActiveTable: React.FC<StudyTableProps> = ({ studyTypes, studyMetadataFilters }) => {
  const [filterOptions, setFilterOptions] = useState<Array<FilterOption>>([]);
  const [columns, setColumns] = useState<Array<TanStackColumnDef<Study>>>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [apiError, setAPIError] = useState<boolean>(false);
  const [studyIDVisible, setStudyIDVisible] = useState<boolean>(false);
  const navigate = useNavigate();

  const { collection: studyForms, collectionLoading: studyFormsFetching } = useFetchCollection<StudyForm>({
    collectionType: 'teamStudiesForms',
    queryParams: { perPage: -1 },
  });

  useEffect(() => {
    if (_isNotEmpty(studyTypes)) {
      setFilterOptions(studiesfilterOptions(studyTypes, studyMetadataFilters));
    }
  }, [studyTypes, studyMetadataFilters]);

  useEffect(() => {
    if (_notNil(studyMetadataFilters) && !studyFormsFetching) {
      generateStudiesMetadataColumns(studyMetadataFilters)
        .then((metadata) => {
          setColumns(
            generateColumns(
              initColumns,
              metadata.map((m) => ({ id: m.id, header: m.header ?? '' })),
              navigate,
              studyForms
            )
          );
          setLoading(false);
        })
        .catch((error) => {
          if (notAborted(error)) {
            setLoading(false);
            setAPIError(error);
          }
        });
    }
  }, [studyMetadataFilters, studyFormsFetching]);

  if (loading) {
    return null;
  }

  return (
    <>
      {apiError ? (
        <ApiErrorBanner className="mb4" errorType={ApiErrorType.FETCH} error={apiError} />
      ) : (
        <div className="pa4">
          <APITable
            columns={columns}
            apiInfo={{ type: 'legacyInternalApi', route: apiRoutes('studies.active') }}
            filterOptions={filterOptions}
            customFilterProcessor={createMultiSelectDoubleEncodingProcessor(studyMetadataFilters ?? [])}
            onFetchTableData={(studies) => {
              if (!studyIDVisible) {
                const updatedColumns = updateColumns(studies, columns);
                if (_isNotEmpty(updatedColumns)) {
                  setStudyIDVisible(true);
                  setColumns(updatedColumns);
                }
              }
            }}
            searchPlaceholderText="Search by study name or study code"
          />
        </div>
      )}
    </>
  );
};

export default ActiveTable;
